import { SelectOption } from "./useSelectState";

export interface UseSelectKeyboardParams {
  name: string;
  enableEnterKeySelection: boolean;
  isFocused: boolean;
  filteredOptions: string[];
  filteredData?: SelectOption[];
  activeOptionIndex: number;
  setActiveOptionIndex: React.Dispatch<React.SetStateAction<number>>;
  selectedValue: string;
  query: string;
  onSelect: (optionValue: string | null) => void;
  ensureDisplayNameInQuery: (optionValue: string) => void;
  onNextField?: () => void;
  onSubmit?: () => void;
  searchable?: boolean;
  isOpen?: boolean;
  close?: () => void;
}

/**
 * Submits the form: invokes the provided `onSubmit`, or falls back to
 * dispatching a native submit event on the first form in the document.
 *
 * @param onSubmit Optional explicit submit callback.
 */
export function submitForm(onSubmit?: () => void): void {
  if (onSubmit) {
    onSubmit();
    return;
  }
  const formElement = document.querySelector('form');
  if (formElement) {
    formElement.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }
}

/**
 * Focuses the next focusable field after the current input on the next animation
 * frame, cancelling any previously scheduled navigation.
 *
 * @param currentInput The input the user is currently on.
 * @param timeoutRef Ref holding the pending requestAnimationFrame id.
 */
export function navigateToNextField(
  currentInput: HTMLInputElement,
  timeoutRef: React.MutableRefObject<number | null>,
): void {
  if (timeoutRef.current !== null) cancelAnimationFrame(timeoutRef.current);
  timeoutRef.current = requestAnimationFrame(() => {
    const allElements = document.querySelectorAll('input, select, textarea, button, [tabindex]:not([tabindex="-1"])');
    const currentIndex = Array.from(allElements).indexOf(currentInput);
    const nextElement = allElements[currentIndex + 1] as HTMLElement;
    if (nextElement) nextElement.focus();
    timeoutRef.current = null;
  });
}
